export const DEFAULT_OZONETEL_SDK_URL = 'https://dc-sdk.cloudagent.ozonetel.com/digitalConsole.js';

export const OZONETEL_SDK_URL =
  process.env.NEXT_PUBLIC_OZONETEL_SDK_URL ?? DEFAULT_OZONETEL_SDK_URL;

export const OZONETEL_SCRIPT_SELECTOR = 'script[data-ua-ozonetel-sdk]';

export const OZONE_TEL_RELEASE_AGENT_MODE = 'MANUAL' as const;
export const OZONE_TEL_MANUAL_CALL_UUI = 'uui' as const;
export const OZONE_TEL_AGENT_MODES = [
  'INBOUND',
  'MANUAL',
  'PROGRESSIVE',
  'PREVIEW',
  'DIGITAL',
  'BLENDED',
] as const;
export const OZONE_TEL_AGENT_STATUSES = ['BUSY', 'READY', 'IDLE'] as const;

export interface OzonetelInitializeEventPayload {
  agentid: string;
  phoneNumber: string;
  pin: number;
}

export interface OzonetelInitializePayload extends OzonetelInitializeEventPayload {
  customer: string;
  sip: true;
}

export type OzonetelReleaseAgentMode = typeof OZONE_TEL_RELEASE_AGENT_MODE;
export type AgentMode = (typeof OZONE_TEL_AGENT_MODES)[number];
export type AgentStatus = (typeof OZONE_TEL_AGENT_STATUSES)[number];
export type OzonetelCallStatus = 'UNAUTHORIZED' | 'IDLE' | 'IN_CALL' | 'HOLD' | 'MUTE' | 'TRANSFER';

export type CallHistory = {
  Status: 'Fail' | 'Success';
  ucid: number;
  audioFile: string;
  callDate: string;
  customerAnswerTime: string;
  endTime: string;
  dest: string;
};

export type OzonetelHandlerName =
  | 'onDropCall'
  | 'onAgentRelease'
  | 'onAgentAnswered'
  | 'onNewCall'
  | 'onTranscribeText'
  | 'onCallDetails';

export interface OzonetelStartCallPayload {
  campaignId: string;
  phoneNumber: number;
  redial?: boolean;
}

export interface DigitalConsoleType {
  initialize: (payload: OzonetelInitializePayload) => Promise<unknown> | unknown;
  logout?: (customer: string, agentid: string) => Promise<unknown> | unknown;
  isAgentLoggedIn?: () => boolean;
  isWebsocketConnected?: () => boolean;
  /** Re-opens the agent websocket from the stored agent data, without a fresh logout/login round trip. */
  reconnectDC?: () => Promise<unknown> | unknown;
  onSIPConnected?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onSIPDisconnected?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onSIPRegistered?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onSIPRegistrationFailed?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  setAgentMode?: (
    mode: AgentMode,
    status: AgentStatus,
    reconnect: boolean,
  ) => Promise<unknown> | unknown;
  initManualCall?: (
    campaignId: string,
    phoneNumber: number,
    redial: boolean,
    uui: string,
  ) => Promise<unknown> | unknown;
  holdCall?: (phoneNumberToHold: number) => Promise<unknown> | unknown;
  unHoldCall?: (phoneNumberToHold: number) => Promise<unknown> | unknown;
  muteCall?: () => Promise<unknown> | unknown;
  unMuteCall?: () => Promise<unknown> | unknown;
  hangupCall?: () => Promise<unknown> | unknown;
  getCallData?: () => { callerId?: string } & Record<string, unknown>;
  getAgentCallHistory?: () => Promise<{ callHist?: string } & Record<string, unknown>>;
  onDropCall?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onAgentRelease?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onAgentCallAnswered?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onNewCall?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onTranscribeText?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  onCallDetails?: (callback: (payload: unknown) => unknown) => Promise<unknown> | unknown;
  releaseAgent?: (mode: OzonetelReleaseAgentMode) => Promise<unknown> | unknown;
}

export const OZONE_TEL_HANDLER_METHODS = {
  onDropCall: 'onDropCall',
  onAgentRelease: 'onAgentRelease',
  onAgentAnswered: 'onAgentCallAnswered',
  onNewCall: 'onNewCall',
  onTranscribeText: 'onTranscribeText',
  onCallDetails: 'onCallDetails',
} as const satisfies Record<OzonetelHandlerName, keyof DigitalConsoleType>;

/**
 * `onSIPConnected` is deliberately absent: it fires when the JsSIP transport opens, which is still
 * before the REGISTER completes. Only a registered softphone can carry an outbound call.
 */
export const OZONE_TEL_SIP_LIFECYCLE_METHODS = [
  'onSIPDisconnected',
  'onSIPRegistered',
  'onSIPRegistrationFailed',
] as const satisfies (keyof DigitalConsoleType)[];

export type OzonetelSipLifecycleMethod = (typeof OZONE_TEL_SIP_LIFECYCLE_METHODS)[number];

export interface OzonetelWindow extends Window {
  dc?: DigitalConsoleType;
  DigitalConsole?: new () => DigitalConsoleType;
  __UA_OZONETEL_SDK_LOADING__?: Promise<void>;
  __UA_OZONETEL_EVENT_BINDINGS__?: Partial<Record<OzonetelHandlerName, true>>;
  __UA_OZONETEL_SIP_BINDINGS__?: Partial<Record<OzonetelSipLifecycleMethod, true>>;
}
